import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { 
  AlertTriangle, 
  RefreshCw, 
  WifiOff, 
  Server, 
  Shield,
  Home,
  Clock
} from 'lucide-react';
import { ErrorType, type AppError } from '@/lib/errors';
import { useNetworkStatus } from '@/hooks/useNetworkStatus';
import { RetryButton } from '@/components/RetryButton';
import { OfflineIndicator } from '@/components/OfflineIndicator';
import { cn } from '@/lib/utils';

interface ErrorRecoveryProps {
  error: AppError | Error | string;
  onRetry?: () => Promise<void> | void;
  onDismiss?: () => void;
  onGoHome?: () => void;
  className?: string;
  variant?: 'inline' | 'card' | 'page';
  showDetails?: boolean;
  autoRetry?: boolean;
  maxAutoRetries?: number;
}

export function ErrorRecovery({
  error,
  onRetry,
  onDismiss,
  onGoHome,
  className,
  variant = 'card',
  showDetails = false,
  autoRetry = false,
  maxAutoRetries = 3,
}: ErrorRecoveryProps) {
  const [autoRetryCount, setAutoRetryCount] = useState(0);
  const [isAutoRetrying, setIsAutoRetrying] = useState(false);
  const networkStatus = useNetworkStatus();

  const getErrorInfo = (error: AppError | Error | string) => {
    if (typeof error === 'string') {
      return {
        title: 'Error',
        message: error,
        icon: AlertTriangle,
        type: ErrorType.UNKNOWN_ERROR,
        color: 'destructive',
        canRetry: true,
      };
    }

    if (error instanceof Error && 'type' in error) {
      const appError = error as AppError;
      switch (appError.type) {
        case ErrorType.NETWORK_ERROR:
          return {
            title: 'Network Error',
            message: 'Unable to connect to the server. Please check your connection.',
            icon: WifiOff,
            type: appError.type,
            color: 'warning',
            canRetry: true,
          };
        case ErrorType.AUTHENTICATION_ERROR:
          return {
            title: 'Authentication Required',
            message: 'Please log in to continue.',
            icon: Shield,
            type: appError.type,
            color: 'destructive',
            canRetry: false,
          };
        case ErrorType.SESSION_NOT_FOUND:
          return {
            title: 'Session Not Found',
            message: 'Your session has expired. Please start a new conversation.',
            icon: Clock,
            type: appError.type,
            color: 'warning',
            canRetry: false,
          };
        case ErrorType.AGENT_ERROR:
          return {
            title: 'Agent Error',
            message: 'The AI agent encountered an error. Please try again.',
            icon: Server,
            type: appError.type,
            color: 'destructive',
            canRetry: true,
          };
        case ErrorType.VALIDATION_ERROR:
          return {
            title: 'Validation Error',
            message: appError.message,
            icon: AlertTriangle,
            type: appError.type,
            color: 'warning',
            canRetry: false,
          };
        default:
          return {
            title: 'Unknown Error',
            message: appError.message,
            icon: AlertTriangle,
            type: appError.type,
            color: 'destructive',
            canRetry: true,
          };
      }
    }

    return {
      title: 'Error',
      message: error.message || 'An unexpected error occurred',
      icon: AlertTriangle,
      type: ErrorType.UNKNOWN_ERROR,
      color: 'destructive',
      canRetry: true,
    };
  };

  const errorInfo = getErrorInfo(error);
  const Icon = errorInfo.icon;

  // Auto-retry logic
  useEffect(() => {
    if (!autoRetry || !onRetry || !errorInfo.canRetry || autoRetryCount >= maxAutoRetries) {
      return;
    }

    // Don't auto-retry if offline for network errors
    if (errorInfo.type === ErrorType.NETWORK_ERROR && !networkStatus.isOnline) {
      return;
    }

    const retryDelay = Math.min(1000 * Math.pow(2, autoRetryCount), 10000); // Exponential backoff, max 10s
    
    setIsAutoRetrying(true);
    const timer = setTimeout(async () => {
      try {
        await onRetry();
        setAutoRetryCount(0); // Reset on success
      } catch (retryError) {
        setAutoRetryCount(prev => prev + 1);
      } finally {
        setIsAutoRetrying(false);
      }
    }, retryDelay);

    return () => clearTimeout(timer);
  }, [autoRetry, onRetry, errorInfo.canRetry, errorInfo.type, autoRetryCount, maxAutoRetries, networkStatus.isOnline]);

  const handleManualRetry = async () => {
    if (!onRetry) return;
    
    try {
      await onRetry();
      setAutoRetryCount(0); // Reset auto-retry count on manual success
    } catch (retryError) {
      console.error('Manual retry failed:', retryError);
    }
  };

  const getColorClasses = (color: string) => {
    switch (color) {
      case 'warning':
        return 'border-orange-200 bg-orange-50 text-orange-900 dark:border-orange-800 dark:bg-orange-900 dark:text-orange-100';
      case 'destructive':
        return 'border-red-200 bg-red-50 text-red-900 dark:border-red-800 dark:bg-red-900 dark:text-red-100';
      default:
        return 'border-destructive bg-destructive/10 text-destructive';
    }
  };

  if (variant === 'page') {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-background">
        <Card className="max-w-lg w-full">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 w-12 h-12 rounded-full bg-destructive/10 flex items-center justify-center">
              <Icon className="h-6 w-6 text-destructive" />
            </div>
            <CardTitle className="text-xl">{errorInfo.title}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <Alert className={getColorClasses(errorInfo.color)}>
              <Icon className="h-4 w-4" />
              <AlertDescription>{errorInfo.message}</AlertDescription>
            </Alert>

            {/* Network status indicator */}
            {errorInfo.type === ErrorType.NETWORK_ERROR && (
              <OfflineIndicator variant="alert" />
            )}

            {/* Auto-retry status */}
            {isAutoRetrying && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <RefreshCw className="h-4 w-4 animate-spin" />
                <span>Retrying automatically... ({autoRetryCount + 1}/{maxAutoRetries})</span>
              </div>
            )}

            <div className="flex gap-2 justify-center">
              {errorInfo.canRetry && onRetry && (
                <RetryButton
                  onRetry={handleManualRetry}
                  disabled={isAutoRetrying}
                  maxRetries={5}
                  checkNetwork={errorInfo.type === ErrorType.NETWORK_ERROR}
                >
                  Try Again
                </RetryButton>
              )}
              
              <Button 
                onClick={onGoHome || (() => window.location.href = '/')}
                variant="outline"
              >
                <Home className="h-4 w-4 mr-2" />
                Go Home
              </Button>
            </div>

            {showDetails && error instanceof Error && (
              <details className="mt-4">
                <summary className="cursor-pointer text-sm font-medium text-muted-foreground">
                  Error Details
                </summary>
                <pre className="mt-2 p-2 bg-muted rounded text-xs overflow-auto">
                  {error.stack}
                </pre>
              </details>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  if (variant === 'card') {
    return (
      <Card className={cn('border-destructive', className)}>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-destructive">
              <Icon className="h-5 w-5" />
              {errorInfo.title}
            </div>
            {autoRetryCount > 0 && (
              <Badge variant="secondary" className="text-xs">
                Retry {autoRetryCount}/{maxAutoRetries}
              </Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-muted-foreground">{errorInfo.message}</p>
          
          {/* Network status for network errors */}
          {errorInfo.type === ErrorType.NETWORK_ERROR && (
            <OfflineIndicator variant="minimal" />
          )}

          {/* Auto-retry indicator */}
          {isAutoRetrying && (
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <RefreshCw className="h-3 w-3 animate-spin" />
              <span>Auto-retrying...</span>
            </div>
          )}
          
          <div className="flex gap-2">
            {errorInfo.canRetry && onRetry && (
              <RetryButton
                onRetry={handleManualRetry}
                disabled={isAutoRetrying}
                size="sm"
                checkNetwork={errorInfo.type === ErrorType.NETWORK_ERROR}
              />
            )}
            
            {onDismiss && (
              <Button onClick={onDismiss} size="sm" variant="ghost">
                Dismiss
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    );
  }

  // Inline variant (default)
  return (
    <Alert className={cn(getColorClasses(errorInfo.color), className)}>
      <Icon className="h-4 w-4" />
      <AlertDescription className="flex items-center justify-between">
        <div className="flex-1">
          <div className="font-medium">{errorInfo.title}</div>
          <div className="mt-1">{errorInfo.message}</div>
          
          {/* Auto-retry status */}
          {isAutoRetrying && (
            <div className="flex items-center gap-1 mt-2 text-xs opacity-70">
              <RefreshCw className="h-3 w-3 animate-spin" />
              <span>Retrying...</span>
            </div>
          )}
        </div>
        
        <div className="flex gap-2 ml-4">
          {errorInfo.canRetry && onRetry && (
            <RetryButton
              onRetry={handleManualRetry}
              disabled={isAutoRetrying}
              size="sm"
              variant="ghost"
              checkNetwork={errorInfo.type === ErrorType.NETWORK_ERROR}
            />
          )}
          
          {onDismiss && (
            <Button onClick={onDismiss} size="sm" variant="ghost">
              Dismiss
            </Button>
          )}
        </div>
      </AlertDescription>
    </Alert>
  );
}